import { useInstallTicker } from "../lib/useInstallTicker.js";

/**
 * Live install counter. Reads a build-time baseline (from
 * src/lib/install-baseline.json, written by scripts/fetch-install-baseline.mjs)
 * and ticks upward at the per-second rate derived from last-month
 * npm download data. Hits /api/installs once on mount to true-up the
 * rate against current registry data.
 *
 * Two rendering modes:
 *   - inline: a compact mono pill suitable for the FinalCTA's tag row
 *   - block:  a centered display with the number large + a caption
 *             underneath, intended for the Hero supporting-proof row
 */
export function InstallTicker({ variant = "block" }: { variant?: "block" | "inline" }) {
  const { value, reduceMotion } = useInstallTicker();
  const formatted = value.toLocaleString();

  if (variant === "inline") {
    return (
      <span
        className="ax-install-ticker-inline"
        title={
          reduceMotion
            ? "Animation disabled — number reflects last build."
            : "Live count across @axon-trading/cli, @axon-trading/sdk, @axon-trading/mcp on npm."
        }
      >
        <span className="ax-install-ticker-inline-num">{formatted}</span> installs
        <style>{`
          .ax-install-ticker-inline { display: inline-flex; align-items: baseline; gap: 5px; }
          .ax-install-ticker-inline-num {
            font-family: var(--at-font-mono);
            font-weight: 600;
            color: var(--at-accent);
          }
        `}</style>
      </span>
    );
  }

  return (
    <div
      className="ax-install-ticker"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={`${formatted} installs across CLI, MCP, and SDK on npm`}
    >
      <span className="ax-install-ticker-num">{formatted}</span>
      <span className="ax-install-ticker-cap">
        installs · CLI + MCP + SDK on npm
      </span>
      <style>{`
        .ax-install-ticker {
          display: inline-flex;
          align-items: baseline;
          gap: 10px;
          padding: 7px 14px;
          border: 1px solid var(--at-hairline);
          background: var(--at-surface);
          border-radius: var(--at-radius-full);
          font-family: var(--at-font-mono);
          font-size: 11px;
          color: var(--at-fg-dim);
          letter-spacing: 0.02em;
        }
        .ax-install-ticker-num {
          font-size: 13px;
          font-weight: 700;
          color: var(--at-accent);
          font-variant-numeric: tabular-nums;
          /* tabular-nums prevents digit width changes from jittering
             the rest of the chip horizontally as the counter ticks. */
        }
        .ax-install-ticker-cap {
          font-size: 11px;
          color: var(--at-fg-dim);
        }
        @media (max-width: 600px) {
          .ax-install-ticker { font-size: 10px; padding: 6px 12px; }
          .ax-install-ticker-num { font-size: 12px; }
        }
      `}</style>
    </div>
  );
}
